import React, { useEffect, useState } from "react";
import { Box, Grid2, IconButton, Theme, Tooltip, useTheme } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import {
  formatShipmentBoxPieceSeq,
  getShipmentBoxAwb,
  getShipmentBoxLabelS3Key,
  isShipmentBoxLocked,
  ShipmentBox,
} from "../../../../../models/Shipments/ShipmentBoxes";
import { getModernPageColors } from "../../../../../styles/modernListingPageSx";
import LabelSecondary from "./LabelSecondary";

interface ShipmentBoxListMetaProps {
  box: ShipmentBox;
}

const MetaField: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);

  return (
    <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
      <CustomTypography
        variant="caption"
        sx={{
          display: "block",
          mb: 0.35,
          color: colors.subtitle,
          fontWeight: 500,
        }}
      >
        {label}
      </CustomTypography>
      {children}
    </Box>
  );
};

const BoxAwbValue: React.FC<{ awb: string }> = ({ awb }) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = async (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    try {
      await navigator.clipboard.writeText(awb);
      setCopied(true);
    } catch {
      // Clipboard may be blocked; keep the AWB visible either way.
    }
  };

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <CustomTypography
        variant="body2"
        fontWeight={600}
        title={awb}
        sx={{
          color: colors.title,
          wordBreak: "break-word",
          minWidth: 0,
        }}
      >
        {awb}
      </CustomTypography>
      <Tooltip title={copied ? "Copied" : "Copy AWB"}>
        <IconButton
          size="small"
          onClick={(event) => {
            void handleCopy(event);
          }}
          aria-label={`Copy AWB ${awb}`}
          sx={{
            p: 0.3,
            flexShrink: 0,
            color: copied
              ? theme.palette.customColors.purpleMain
              : theme.palette.customColors.greyTextColor,
          }}
        >
          {copied ? (
            <CheckIcon sx={{ fontSize: 12 }} />
          ) : (
            <ContentCopyOutlinedIcon sx={{ fontSize: 12 }} />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const ShipmentBoxListMeta: React.FC<ShipmentBoxListMetaProps> = ({ box }) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);
  const awb = getShipmentBoxAwb(box);
  const pieceSeq = formatShipmentBoxPieceSeq(box);
  const labelS3Key = getShipmentBoxLabelS3Key(box);
  const locked = isShipmentBoxLocked(box);
  const hasMeta = Boolean(awb || pieceSeq || labelS3Key);

  if (!locked && !hasMeta) {
    return null;
  }

  return (
    <Box sx={{ mt: 1, minWidth: 0, maxWidth: "100%" }}>
      {locked && (
        <CustomTypography
          variant="caption"
          sx={{
            color: colors.subtitle,
            display: "block",
            mb: hasMeta ? 1 : 0,
          }}
        >
          AWB assigned
        </CustomTypography>
      )}
      {hasMeta && (
        <Grid2 container spacing={1.25} columns={{ xs: 12, sm: 12, md: 12 }}>
          {awb ? (
            <Grid2 size={{ xs: 12, sm: 6, md: 4 }} sx={{ minWidth: 0 }}>
              <MetaField label="AWB">
                <BoxAwbValue awb={awb} />
              </MetaField>
            </Grid2>
          ) : null}
          {pieceSeq ? (
            <Grid2 size={{ xs: 12, sm: 6, md: 4 }} sx={{ minWidth: 0 }}>
              <MetaField label="Piece seq">
                <CustomTypography
                  variant="body2"
                  fontWeight={600}
                  sx={{ color: colors.title, wordBreak: "break-word" }}
                >
                  {pieceSeq}
                </CustomTypography>
              </MetaField>
            </Grid2>
          ) : null}
          {labelS3Key ? (
            <Grid2 size={{ xs: 12, sm: 6, md: 4 }} sx={{ minWidth: 0 }}>
              <MetaField label="Shipping label">
                <Box
                  sx={{
                    minWidth: 0,
                    maxWidth: "100%",
                    overflow: "hidden",
                  }}
                >
                  <LabelSecondary
                    labelS3Key={labelS3Key}
                    emptyFallback="—"
                    fontSize="0.8rem"
                  />
                </Box>
              </MetaField>
            </Grid2>
          ) : null}
        </Grid2>
      )}
    </Box>
  );
};

export default ShipmentBoxListMeta;
